import React, { useState, useEffect, useMemo } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as ChevronDown } from "bootstrap-icons/icons/chevron-down.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import axios from "axios";
import ApiServices from "../../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomStyles from "../../../components/utils/CustomStyles";
import formatDate from "../../../components/utils/DateFormatter";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import AxiosApiCall from "../../../api/AxiosApiCall";
import { downloadCSVFromApi } from "../../../components/utils/DownlaodCSVFromApi";

const GetBillsProducts = () => {
  const [airtimeProducts, setAirtimeProducts] = useState([]);
  const [dataProducts, setDataProducts] = useState([]);
  const [discoProducts, setDiscoProducts] = useState([]);
  const [tvProducts, setTvProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [filterTextAirtime, setFilterTextAirtime] = useState("");
  const [filterTextData, setFilterTextData] = useState("");
  const [filterTextDisco, setFilterTextDisco] = useState("");
  const [filterTextTv, setFilterTextTv] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProductBillsType, setSelectedProductBillsType] =
    useState("airtime");

  // Date filter state
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest("#menu-button")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isDropdownOpen]);

  //axios api encryption call
  const axios = AxiosApiCall();

  const fetchBillProductList = async (type) => {
    setLoading(true);
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;

      // Extract the actual token part
      const actualToken = token.split("|")[1];

      const response = await axios.post(
        ApiServices.getBillsProduct,
        {
          // Send the selected type
          product: type,
        },
        {
          headers: {
            Authorization: `Bearer ${actualToken}`,
            token: token,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const { status, message, data } = response.data;
      if (status === 200) {
        switch (type) {
          case "airtime":
            setAirtimeProducts(data);
            break;
          case "data":
            setDataProducts(data);
            break;
          case "disco":
            setDiscoProducts(data);
            break;
          case "tv":
            setTvProducts(data);
            break;
          default:
            break;
        }
      } else {
        console.log("Error:", message);
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      console.log("Error occurred:", error.message);
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data based on selection
    fetchBillProductList(selectedProductBillsType);
  }, [selectedProductBillsType]);

  const handleFilter = (e, type) => {
    switch (type) {
      case "airtime":
        setFilterTextAirtime(e.target.value);
        break;
      case "data":
        setFilterTextData(e.target.value);
        break;
      case "disco":
        setFilterTextDisco(e.target.value);
        break;
      case "tv":
        setFilterTextTv(e.target.value);
        break;
      default:
        break;
    }
    setResetPaginationToggle(!resetPaginationToggle);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Check if both dates are selected
  const areDatesSelected = filters.start_date && filters.end_date;

  // Handle CSV Download
  const handleDownloadCSV = async () => {
    if (!selectedProductBillsType) {
      toast.error("Please select a product type to download CSV");
      return;
    }
    if (!areDatesSelected) {
      toast.error("Please select both start and end dates to download CSV");
      return;
    }

    // Validate date range
    const startDate = new Date(filters.start_date);
    const endDate = new Date(filters.end_date);

    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }

    // limit date range to a year
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      toast.error("Date range cannot exceed 1 year");
      return;
    }

    setLoadingCSV(true);

    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;

      if (!token) {
        toast.error("No access token found");
        return;
      }

      // Extract the actual token part
      const actualToken = token.split("|")[1];

      // Prepare download payload
      const downloadPayload = {
        product: selectedProductBillsType,
        start_date: filters.start_date,
        end_date: filters.end_date,
        download: 1,
      };

      // Create filename with date range
      const startDateFormatted = filters.start_date.replace(/-/g, '_');
      const endDateFormatted = filters.end_date.replace(/-/g, '_');
      const filename = `${selectedProductBillsType}_bills_${startDateFormatted}_to_${endDateFormatted}.csv`;

      // Call the download function with navigate parameter
      await downloadCSVFromApi(
        ApiServices.getBillsProduct,
        downloadPayload,
        actualToken,
        {
          filename: filename,
          columnsToRemove: [], // Add sensitive columns to exclude 
          maxRows: 50000,
          navigate: navigate,
          method: "POST",
        }
      );

    } catch (error) {
      console.error("Download error:", error);
      if (error.message && error.message.includes("CSV")) {
        toast.error("Failed to process CSV file");
      } else if (error.message && error.message.includes("network")) {
        toast.error("Network error during download");
      } else {
        toast.error("Download failed");
      }
    } finally {
      setLoadingCSV(false);
    }
  };

  // Clear date filters
  const handleClearDates = () => {
    setFilters({
      start_date: "",
      end_date: "",
    });
    toast.success("Date filters cleared");
  };

  // Convert numeric status to human-readable text
  const getStatusText = (status) => {
    switch (status) {
      case "1":
        return "successful";
      case "2":
        return "failed";
      case "0":
      case "3":
        return "pending";
      default:
        return "";
    }
  };

  // Filter data based on search text AND date range for each product type
  const getFilteredProducts = (products, filterText) => {
    return products.filter((product) => {
      // Search filter
      const matchesFilterText = Object.values(product).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(filterText.toLowerCase())
      );

      // Filter data based on date range
      let matchesDateRange = true;
      if (areDatesSelected && product.created_at) {
        const itemDate = new Date(product.created_at);
        const startDate = new Date(filters.start_date);
        const endDate = new Date(filters.end_date);
        endDate.setHours(23, 59, 59, 999);
        matchesDateRange = itemDate >= startDate && itemDate <= endDate;
      }

      return matchesFilterText && matchesDateRange;
    });
  };

  // Apply filtering for each product type
  const filteredAirtimeProducts = useMemo(() =>
    getFilteredProducts(airtimeProducts, filterTextAirtime),
    [airtimeProducts, filterTextAirtime, filters, areDatesSelected]
  );

  const filteredDataProducts = useMemo(() =>
    getFilteredProducts(dataProducts, filterTextData),
    [dataProducts, filterTextData, filters, areDatesSelected]
  );

  const filteredDiscoProducts = useMemo(() =>
    getFilteredProducts(discoProducts, filterTextDisco),
    [discoProducts, filterTextDisco, filters, areDatesSelected]
  );

  const filteredTvProducts = useMemo(() =>
    getFilteredProducts(tvProducts, filterTextTv),
    [tvProducts, filterTextTv, filters, areDatesSelected]
  );

  const handleCustomerTypeSelect = (type) => {
    setSelectedProductBillsType(type);
    setIsDropdownOpen(false);
  };


  // Add this function to create columns with view more action
  const getColumns = (type) => {
    // Base columns for each type
    const baseColumns = {
      airtime: airtimeColumns,
      data: dataColumns,
      disco: discoColumns,
      tv: tvColumns
    };

    // Add actions column to the existing columns
    const actionsColumn = {
      name: "Actions",
      cell: (row) => (
        <button
          onClick={() => navigate(`/admin/billers/list/details`, {
            state: {
              transaction: row,
              type: type
            }
          })}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-darkGreen3 rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
          title="View Details"
        >
          <Eye className="w-4 h-4 mr-1.5" />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "100px",
    };

    return [...baseColumns[type], actionsColumn];
  };
  //table column

  const airtimeColumns = [
    {
      name: "Request Id",
      selector: (row) => (row.request_id ? row.request_id : "NOT AVAILABLE"),
      sortable: true,
      width: "150px",
    },
    {
      name: "Phone Number",
      selector: (row) =>
        row.phone_number ? row.phone_number : "NOT AVAIALABLE",
      sortable: true,
      width: "150px",
    },
    {
      name: "Amount",
      selector: (row) => (row.amount ? CurrencyFormatter(row.amount) : "0.00"),
      sortable: true,
      width: "180px",
    },
    {
      name: "Network",
      selector: (row) =>
        row.network ? row.network.toUpperCase() : "NOT AVAILABLE",
      sortable: true,
      width: "150px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        let backgroundColor;
        let textColor;

        switch (row.status) {
          case "1":
            backgroundColor = "bg-darkGreen3";
            textColor = "text-dGrey3";
            break;
          case "2":
            backgroundColor = "bg-dash3";
            textColor = "text-dGrey3";
            break;
          case "0":
          case "3":
            backgroundColor = "bg-dash5";
            textColor = "text-dGrey3";
            break;
          default:
            backgroundColor = "bg-grey1";
            textColor = "text-dGrey3";
        }

        return (
          <div className={`p-1 rounded ${backgroundColor} ${textColor}`}>
            {row.status === "1"
              ? "Successful"
              : row.status === "2"
                ? "Failed"
                : "Pending"}
          </div>
        );
      },
      width: "150px",
    },
    {
      name: "Date",
      selector: (row) =>
        row.created_at ? formatDate(row.created_at) : "NOT AVAILABLE",
      sortable: true,
      width: "200px",
    },
  ];

  const dataColumns = [
    {
      name: "Request Id",
      selector: (row) => (row.request_id ? row.request_id : "NOT AVAILABLE"),
      sortable: true,
      width: "200px",
    },
    {
      name: "Phone Number",
      selector: (row) =>
        row.phone_number ? row.phone_number : "NOT AVAIALABLE",
      sortable: true,
      width: "150px",
    },
    {
      name: "Amount",
      selector: (row) => (row.amount ? CurrencyFormatter(row.amount) : "0.00"),
      sortable: true,
      width: "180px",
    },
    {
      name: "Network",
      selector: (row) =>
        row.network ? row.network.toUpperCase() : "NOT AVAILABLE",
      sortable: true,
      width: "150px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        let backgroundColor;
        let textColor;

        switch (row.status) {
          case "1":
            backgroundColor = "bg-darkGreen3";
            textColor = "text-dGrey3";
            break;
          case "2":
            backgroundColor = "bg-dash3";
            textColor = "text-dGrey3";
            break;
          case "0":
          case "3":
            backgroundColor = "bg-dash5";
            textColor = "text-dGrey3";
            break;
          default:
            backgroundColor = "bg-grey1";
            textColor = "text-dGrey3";
        }

        return (
          <div className={`p-1 rounded ${backgroundColor} ${textColor}`}>
            {row.status === "1"
              ? "Successful"
              : row.status === "2"
                ? "Failed"
                : "Pending"}
          </div>
        );
      },
      width: "150px",
    },
    {
      name: "Date",
      selector: (row) =>
        row.created_at ? formatDate(row.created_at) : "NOT AVAILABLE",
      sortable: true,
      width: "200",
    },
  ];

  const discoColumns = [
    {
      name: "Request Id",
      selector: (row) => (row.request_id ? row.request_id : "NOT AVAILABLE"),
      sortable: true,
      width: "180px",
    },
    {
      name: "Customer Name",
      selector: (row) => (row.name ? row.name.toUpperCase() : "NOT AVAIALABLE"),
      sortable: true,
      width: "230px",
    },
    {
      name: "Meter Number",
      selector: (row) => (row.meterNo ? row.meterNo : "NOT AVAIALABLE"),
      sortable: true,
      width: "170px",
    },
    {
      name: "Type",
      selector: (row) => (row.type ? row.type.toUpperCase() : "NOT AVAILABLE"),
      sortable: true,
      width: "180",
    },
    {
      name: "Amount",
      selector: (row) => (row.amount ? CurrencyFormatter(row.amount) : "0.00"),
      sortable: true,
      width: "180px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        let backgroundColor;
        let textColor;

        switch (row.status) {
          case "1":
            backgroundColor = "bg-darkGreen3";
            textColor = "text-dGrey3";
            break;
          case "2":
            backgroundColor = "bg-dash3";
            textColor = "text-dGrey3";
            break;
          case "0":
          case "3":
            backgroundColor = "bg-dash5";
            textColor = "text-dGrey3";
            break;
          default:
            backgroundColor = "bg-grey1";
            textColor = "text-dGrey3";
        }

        return (
          <div className={`p-1 rounded ${backgroundColor} ${textColor}`}>
            {row.status === "1"
              ? "Successful"
              : row.status === "2"
                ? "Failed"
                : "Pending"}
          </div>
        );
      },
      width: "150px",
    },
    {
      name: "Date",
      selector: (row) =>
        row.created_at ? formatDate(row.created_at) : "NOT AVAILABLE",
      sortable: true,
      width: "180px",
    },
  ];

  const tvColumns = [
    {
      name: "Request Id",
      selector: (row) => (row.request_id ? row.request_id : "NOT AVAILABLE"),
      sortable: true,
      width: "150px",
    },
    {
      name: "Customer Name",
      selector: (row) =>
        row.customerName ? row.customerName.toUpperCase() : "NOT AVAIALABLE",
      sortable: true,
      width: "200px",
    },
    {
      name: "Smart Card No",
      selector: (row) =>
        row.smartcardNumber ? row.smartcardNumber : "NOT AVAIALABLE",
      sortable: true,
      width: "150px",
    },
    {
      name: "Type",
      selector: (row) => (row.type ? row.type.toUpperCase() : "NOT AVAILABLE"),
      sortable: true,
      width: "130px",
    },
    {
      name: "Amount",
      selector: (row) => (row.amount ? CurrencyFormatter(row.amount) : "0.00"),
      sortable: true,
      width: "180px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        let backgroundColor;
        let textColor;

        switch (row.status) {
          case "1":
            backgroundColor = "bg-darkGreen3";
            textColor = "text-dGrey3";
            break;
          case "2":
            backgroundColor = "bg-dash3";
            textColor = "text-dGrey3";
            break;
          case "0":
          case "3":
            backgroundColor = "bg-dash5";
            textColor = "text-dGrey3";
            break;
          default:
            backgroundColor = "bg-grey1";
            textColor = "text-dGrey3";
        }

        return (
          <div className={`p-1 rounded ${backgroundColor} ${textColor}`}>
            {row.status === "1"
              ? "Successful"
              : row.status === "2"
                ? "Failed"
                : "Pending"}
          </div>
        );
      },
      width: "120px",
    },
    {
      name: "Date",
      selector: (row) =>
        row.created_at ? formatDate(row.created_at) : "NOT AVAILABLE",
      sortable: true,
      width: "200px",
    },
  ];

  return (
    <MainLayouts>
      <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
        <section
          className="w-full pl-[3%] pr-[3%] pt-1 my-1"
          style={{ backgroundColor: "#F7F7F7" }}
        >
          <div className="flex justify-between items-center">
            <nav className="bg-gray-200 py-0 px-4 rounded-lg">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Bills Product
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-orange1">List</p>
                </li>
              </ol>
            </nav>
            <button
              id="menu-button"
              className="bg-darkGreen3 inline-flex justify-center gap-x-1.5 rounded-md px-3 py-3  my-3 text-sm font-semibold text-lGrey3 shadow-sm ring-1 ring-inset hover:bg-gray-50"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
            >
              {selectedProductBillsType.toUpperCase()} Products
              <ChevronRightIcon
                className={`${isDropdownOpen ? "rotate-90 transform" : ""
                  } h-5 w-5`}
                aria-hidden="true"
              />
            </button>
          </div>
        </section>

        {/* Filter Controls Section - Responsive */}
        <div className="pb-4 bg-white dark:bg-gray-900">
          {/* DESKTOP FILTERS (md and up) - All in one row */}
          <div className="hidden md:flex flex-wrap items-center gap-3">
            {/* Search Input Field with Icon - Dynamic based on selected type */}
            <div className="relative flex-grow min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="search"
                  className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  placeholder={`Search ${selectedProductBillsType} products...`}
                  value={
                    selectedProductBillsType === "airtime" ? filterTextAirtime :
                      selectedProductBillsType === "data" ? filterTextData :
                        selectedProductBillsType === "disco" ? filterTextDisco :
                          filterTextTv
                  }
                  onChange={(e) => handleFilter(e, selectedProductBillsType)}
                />
              </div>
            </div>

            {/* Start Date Input */}
            <div className="min-w-[150px]">
              <div className="relative">
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange("start_date", e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                  max={filters.end_date || undefined}
                  placeholder="Start Date"
                />
              </div>
            </div>

            {/* End Date Input */}
            <div className="min-w-[150px]">
              <div className="relative">
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange("end_date", e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                  min={filters.start_date || undefined}
                  placeholder="End Date"
                />
              </div>
            </div>

            {/* Clear Dates Button - Only show when dates are selected */}
            {areDatesSelected && (
              <div className="min-w-[120px]">
                <button
                  onClick={handleClearDates}
                  className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                >
                  <XCircle className="w-4 h-4" />
                  Clear Dates
                </button>
              </div>
            )}

            {/* Download Button */}
            <div className="min-w-[160px]">
              <button
                onClick={handleDownloadCSV}
                className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${areDatesSelected
                  ? "bg-orange1 text-lGrey3 hover:bg-orange-600 cursor-pointer"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                disabled={!areDatesSelected || loadingCSV}
                title={!areDatesSelected ? "Select start and end dates to enable download" : ""}
              >
                {loadingCSV ? (
                  <>
                    <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download CSV
                  </>
                )}
              </button>
            </div>
          </div>

          {/* MOBILE FILTERS (below md) - Stacked layout */}
          <div className="md:hidden flex flex-col gap-3">
            {/* Search - Full width on mobile */}
            <div className="w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="search"
                  className="block w-full pl-10 pr-3 py-3 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                  placeholder={`Search ${selectedProductBillsType} products...`}
                  value={
                    selectedProductBillsType === "airtime" ? filterTextAirtime :
                      selectedProductBillsType === "data" ? filterTextData :
                        selectedProductBillsType === "disco" ? filterTextDisco :
                          filterTextTv
                  }
                  onChange={(e) => handleFilter(e, selectedProductBillsType)}
                />
              </div>
            </div>

            {/* Filters grid for mobile */}
            <div className="grid grid-cols-2 gap-3">
              {/* Start Date - Half width on mobile */}
              <div className="col-span-1">
                <div className="relative">
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange("start_date", e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                    max={filters.end_date || undefined}
                    placeholder="Start"
                  />
                </div>
              </div>

              {/* End Date - Half width on mobile */}
              <div className="col-span-1">
                <div className="relative">
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange("end_date", e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                    min={filters.start_date || undefined}
                    placeholder="End"
                  />
                </div>
              </div>

              {/* Clear Dates Button - Full width when shown */}
              {areDatesSelected && (
                <div className="col-span-2">
                  <button
                    onClick={handleClearDates}
                    className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800"
                  >
                    <XCircle className="w-4 h-4" />
                    Clear Dates
                  </button>
                </div>
              )}

              {/* Download Button - Full width on mobile */}
              <div className="col-span-2">
                <button
                  onClick={handleDownloadCSV}
                  className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-3 text-sm font-semibold ${areDatesSelected
                    ? "bg-orange1 text-lGrey3 hover:bg-orange-600"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  disabled={!areDatesSelected || loadingCSV}
                >
                  {loadingCSV ? (
                    <>
                      <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                      <span className="ml-2">Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span className="ml-2">Download CSV</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* DESKTOP help text (md and up) */}
          <div className="hidden md:flex items-center justify-between mt-2">
            {!areDatesSelected && (
              <p className="text-xs text-gray-500">
                Select start and end dates to enable download
              </p>
            )}
            {areDatesSelected && (
              <p className="text-sm text-gray-600">
                {selectedProductBillsType === "airtime" && `Showing ${filteredAirtimeProducts.length} records from ${filters.start_date} to ${filters.end_date}`}
                {selectedProductBillsType === "data" && `Showing ${filteredDataProducts.length} records from ${filters.start_date} to ${filters.end_date}`}
                {selectedProductBillsType === "disco" && `Showing ${filteredDiscoProducts.length} records from ${filters.start_date} to ${filters.end_date}`}
                {selectedProductBillsType === "tv" && `Showing ${filteredTvProducts.length} records from ${filters.start_date} to ${filters.end_date}`}
              </p>
            )}
          </div>

          {/* MOBILE help text (below md) */}
          <div className="md:hidden mt-3">
            {!areDatesSelected && (
              <p className="text-xs text-gray-500 text-center mb-2">
                Select start and end dates to enable download
              </p>
            )}
            {areDatesSelected && (
              <p className="text-sm text-gray-600 text-center">
                {selectedProductBillsType === "airtime" && `Showing ${filteredAirtimeProducts.length} records`}
                {selectedProductBillsType === "data" && `Showing ${filteredDataProducts.length} records`}
                {selectedProductBillsType === "disco" && `Showing ${filteredDiscoProducts.length} records`}
                {selectedProductBillsType === "tv" && `Showing ${filteredTvProducts.length} records`}
              </p>
            )}
          </div>
        </div>

        {/* Dropdown for product type selection */}
        <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap items-center justify-between gap-4">
          <div className="justify-end flex-shrink-0 w-full sm:w-6/12 ">
            {isDropdownOpen && (
              <ul
                className="absolute z-10 mt-2 w-full bg-dGrey3 border border-lGrey rounded-md shadow-lg"
                role="menu"
              >
                {["airtime", "data", "disco", "tv"].map((type) => (
                  <li key={type}>
                    <button
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 w-full text-left hover:bg-orange1"
                      onClick={() => handleCustomerTypeSelect(type)}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)} Products
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <>
            {selectedProductBillsType === "airtime" && (
              <DataTable
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: "#1D9E34",
                    },
                  },
                  headCells: {
                    style: {
                      color: "white",
                    },
                  },
                }}
                columns={getColumns("airtime")}
                data={filteredAirtimeProducts}
                pagination
                striped
                responsive
                highlightOnHover
                paginationResetDefaultPage={resetPaginationToggle}
                noDataComponent={
                  <div className="text-center py-8">
                    {areDatesSelected ?
                      "No airtime products found for the selected date range" :
                      "No airtime products found"
                    }
                  </div>
                }
              />
            )}
            {selectedProductBillsType === "data" && (
              <DataTable
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: "#FFA439",
                    },
                  },
                  headCells: {
                    style: {
                      color: "white",
                    },
                  },
                }}
                columns={getColumns("data")}
                data={filteredDataProducts}
                pagination
                striped
                responsive
                highlightOnHover
                paginationResetDefaultPage={resetPaginationToggle}
                noDataComponent={
                  <div className="text-center py-8">
                    {areDatesSelected ?
                      "No data products found for the selected date range" :
                      "No data products found"
                    }
                  </div>
                }
              />
            )}
            {selectedProductBillsType === "disco" && (
              <DataTable
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: "#4373fc",
                    },
                  },
                  headCells: {
                    style: {
                      color: "white",
                    },
                  },
                }}
                columns={getColumns("disco")}
                data={filteredDiscoProducts}
                pagination
                striped
                responsive
                highlightOnHover
                paginationResetDefaultPage={resetPaginationToggle}
                noDataComponent={
                  <div className="text-center py-8">
                    {areDatesSelected ?
                      "No disco products found for the selected date range" :
                      "No disco products found"
                    }
                  </div>
                }
              />
            )}
            {selectedProductBillsType === "tv" && (
              <DataTable
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: "#313736",
                    },
                  },
                  headCells: {
                    style: {
                      color: "white",
                    },
                  },
                }}
                columns={getColumns("tv")}
                data={filteredTvProducts}
                responsive
                pagination
                paginationResetDefaultPage={resetPaginationToggle}
                noDataComponent={
                  <div className="text-center py-8">
                    {areDatesSelected ?
                      "No TV products found for the selected date range" :
                      "No TV products found"
                    }
                  </div>
                }
              />
            )}
          </>
        )}
      </div>
    </MainLayouts>
  );
};

export default GetBillsProducts;
